import React, { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { useAuth } from '../context/AuthContext.js'
import VersionHistoryPanel from '../components/VersionHistoryPanel.jsx'
import './SafetyRulesEditor.css'

// Rich-text block used inside the PDF document preview.
// Swaps <textarea> for contentEditable so the formatting toolbar (Bold /
// Italic / Underline) can work via document.execCommand.  The trick:
// toolbar buttons use onMouseDown + e.preventDefault() so the editable div
// never loses focus when the user clicks a format button.  innerHTML is
// synced FROM props only while the element is not focused (prevents React
// from clobbering an in-progress edit); changes are reported to the parent
// on blur.
const PdfTextBlock = React.memo(function PdfTextBlock({ initialHTML, onChange }) {
  const divRef = useRef(null)

  useLayoutEffect(() => {
    const el = divRef.current
    if (!el || document.activeElement === el) return
    // Plain text from the PDF import (newlines, no HTML tags) → convert to <br>
    // so the block renders with paragraph breaks; already-HTML content passes through.
    const html = /<[^>]+>/.test(initialHTML || '')
      ? (initialHTML || '')
      : (initialHTML || '').replace(/\n/g, '<br>')
    el.innerHTML = html
  }, [initialHTML])

  // Apply a font-size to the current selection by using fontSize=7 as a
  // marker (execCommand), then immediately swapping the <font> element for a
  // <span style="font-size: X"> so the saved HTML stays clean.
  const applyFontSize = (sizeEm) => {
    const el = divRef.current
    if (!el) return
    document.execCommand('fontSize', false, '7')
    el.querySelectorAll('font[size="7"]').forEach(font => {
      const span = document.createElement('span')
      span.style.fontSize = sizeEm
      while (font.firstChild) span.appendChild(font.firstChild)
      font.replaceWith(span)
    })
  }

  const applyColor = (color) => {
    document.execCommand('foreColor', false, color)
  }

  return (
    <div className="sre-pdf-text-wrap">
      <div className="sre-pdf-text-toolbar">
        <button className="sre-pdf-toolbar-btn" title="Fett (Strg+B)"
          onMouseDown={e => { e.preventDefault(); document.execCommand('bold', false, null) }}
        ><strong>B</strong></button>
        <button className="sre-pdf-toolbar-btn" title="Kursiv (Strg+I)"
          onMouseDown={e => { e.preventDefault(); document.execCommand('italic', false, null) }}
        ><em>I</em></button>
        <button className="sre-pdf-toolbar-btn" title="Unterstrichen (Strg+U)"
          onMouseDown={e => { e.preventDefault(); document.execCommand('underline', false, null) }}
        ><u>U</u></button>
        <button className="sre-pdf-toolbar-btn" title="Aufzählung (Bulletpoints)"
          onMouseDown={e => { e.preventDefault(); document.execCommand('insertUnorderedList', false, null) }}
        >•</button>
        <span className="sre-pdf-toolbar-sep" />
        <button className="sre-pdf-toolbar-btn" title="Klein"
          onMouseDown={e => { e.preventDefault(); applyFontSize('0.8em') }}
        >S</button>
        <button className="sre-pdf-toolbar-btn" title="Normal"
          onMouseDown={e => { e.preventDefault(); applyFontSize('1em') }}
        >M</button>
        <button className="sre-pdf-toolbar-btn" title="Groß"
          onMouseDown={e => { e.preventDefault(); applyFontSize('1.3em') }}
        >L</button>
        <button className="sre-pdf-toolbar-btn" title="Überschrift"
          onMouseDown={e => { e.preventDefault(); applyFontSize('1.6em') }}
        >XL</button>
        <span className="sre-pdf-toolbar-sep" />
        <button className="sre-pdf-toolbar-btn sre-pdf-color-red" title="Rot"
          onMouseDown={e => { e.preventDefault(); applyColor('#e53e3e') }}
        >A</button>
        <button className="sre-pdf-toolbar-btn sre-pdf-color-green" title="Grün"
          onMouseDown={e => { e.preventDefault(); applyColor('#2f855a') }}
        >A</button>
        <button className="sre-pdf-toolbar-btn sre-pdf-color-black" title="Schwarz/Standard"
          onMouseDown={e => { e.preventDefault(); applyColor('#1a1a2e') }}
        >A</button>
        <span className="sre-pdf-toolbar-sep" />
        <button className="sre-pdf-toolbar-btn sre-pdf-mark-yellow" title="Gelb markieren"
          onMouseDown={e => { e.preventDefault(); document.execCommand('hiliteColor', false, '#ffee58') }}
        >M</button>
        <button className="sre-pdf-toolbar-btn sre-pdf-mark-none" title="Markierung entfernen"
          onMouseDown={e => { e.preventDefault(); document.execCommand('hiliteColor', false, 'transparent') }}
        >M</button>
      </div>
      <div
        ref={divRef}
        contentEditable
        suppressContentEditableWarning
        className="sre-pdf-editable-text"
        onBlur={e => onChange(e.currentTarget.innerHTML)}
      />
    </div>
  )
})

const SafetyRulesEditor = () => {
  const { apiFetch, admin, isOwner, features } = useAuth()
  const allowCustomRules = isOwner || features.allow_custom_rules === true

  const [tenants, setTenants]               = useState([])
  const [selectedTenant, setSelectedTenant] = useState('')
  const [rules, setRules]                   = useState([{ library_id: null, custom_text: '', custom_image_url: null, image_only: false }])
  const [ruleLibrary, setRuleLibrary]       = useState([])
  const [loading, setLoading]               = useState(false)
  const [saving, setSaving]                 = useState(false)
  const [skipTranslation, setSkipTranslation] = useState(false)
  const [error, setError]                   = useState(null)
  const [success, setSuccess]               = useState(false)

  // 'grid' | 'document' | null (null = auto-detect from content, see server)
  const [displayOverride, setDisplayOverride] = useState(null)
  // true when the current rule set was imported from a PDF — locks display to document
  const [isPdfImport, setIsPdfImport] = useState(false)

  // Image library picker modal (for custom rules)
  const [showImageModal, setShowImageModal] = useState(false)
  const [activeImageIdx, setActiveImageIdx] = useState(null)
  const [imageLibrary, setImageLibrary]     = useState([])
  const [loadingImages, setLoadingImages]   = useState(false)
  const [uploadingLib, setUploadingLib]     = useState(false)
  const libFileInputRef                     = useRef(null)

  // PDF import (custom text/image blocks)
  const [importingPdf, setImportingPdf]     = useState(false)
  const pdfFileInputRef                     = useRef(null)

  // ── Load tenants ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOwner) {
      apiFetch('/api/admin/tenants')
        .then(r => r.json())
        .then(data => {
          setTenants(data)
          if (data.length > 0) {
            setSelectedTenant(data[0].id)
          }
        })
    } else {
      setSelectedTenant(admin.tenant_id)
    }
  }, [isOwner])

  // ── Load rule library ─────────────────────────────────────────────────────
  useEffect(() => {
    apiFetch('/api/admin/rule-library')
      .then(r => r.json())
      .then(data => setRuleLibrary(Array.isArray(data) ? data : []))
      .catch(() => {})
  }, [])

  // ── Load saved rules for tenant ───────────────────────────────────────────
  const loadRules = () => {
    if (!selectedTenant) return
    setLoading(true)
    setError(null)
    setIsPdfImport(false)
    apiFetch(`/api/admin/safety-rules/${selectedTenant}`)
      .then(r => r.json())
      .then(data => {
        const deRules = data.find(r => r.language === 'de')
        const savedDisplay = deRules?.display_style || null
        setDisplayOverride(savedDisplay)
        setIsPdfImport(savedDisplay === 'document')
        if (deRules?.rules?.length > 0) {
          setRules(deRules.rules.map(r => {
            if (typeof r === 'string') return { library_id: null, custom_text: '', custom_image_url: null, image_only: false }
            const hasText = !!(r.text && r.text.trim())
            return {
              library_id:       r.library_id || null,
              // custom_text is always populated (even for library-linked rules) because
              // the document/PDF view reads it directly regardless of library_id — only
              // the grid view's textarea is gated on !library_id, so this is safe there.
              custom_text:      r.text || '',
              custom_image_url: r.image_url || null,
              image_only:       !r.library_id && !hasText && !!r.image_url,
              image_size:       r.image_size || null,
            }
          }))
        } else {
          setRules([{ library_id: null, custom_text: '', custom_image_url: null, image_only: false }])
        }
      })
      .catch(() => setError('Sicherheitsregeln konnten nicht geladen werden'))
      .finally(() => setLoading(false))
  }

  useEffect(loadRules, [selectedTenant])

  // ── Rule editing ──────────────────────────────────────────────────────────
  const selectRule = (idx, library_id) =>
    setRules(prev => prev.map((r, i) => i === idx ? { library_id: library_id || null, custom_text: '', custom_image_url: null, image_only: false } : r))

  const updateCustomText = (idx, text) =>
    setRules(prev => prev.map((r, i) => i === idx ? { ...r, library_id: null, custom_text: text } : r))

  const updateCustomImageUrl = (idx, url) =>
    setRules(prev => prev.map((r, i) => i === idx ? { ...r, custom_image_url: url } : r))

  const updateImageSize = (idx, size) =>
    setRules(prev => prev.map((r, i) => i === idx ? { ...r, image_size: size } : r))

  const addRule       = () => setRules(prev => [...prev, { library_id: null, custom_text: '', custom_image_url: null, image_only: false }])
  const addImageBlock = () => setRules(prev => [...prev, { library_id: null, custom_text: null, custom_image_url: null, image_only: true }])
  const insertRuleAt  = (idx) => setRules(prev => [
    ...prev.slice(0, idx),
    { library_id: null, custom_text: '', custom_image_url: null, image_only: false },
    ...prev.slice(idx),
  ])

  // ── Image picker for custom rules ─────────────────────────────────────────
  const openImageModal = async (idx) => {
    setActiveImageIdx(idx)
    setShowImageModal(true)
    setLoadingImages(true)
    try {
      const res  = await apiFetch('/api/admin/image-library')
      const data = await res.json()
      setImageLibrary(Array.isArray(data) ? data : [])
    } catch { setImageLibrary([]) }
    finally  { setLoadingImages(false) }
  }

  const resizeImage = (file, maxW = 800, maxH = 600) =>
    new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        URL.revokeObjectURL(url)
        const scale = Math.min(1, maxW / img.width, maxH / img.height)
        const w = Math.round(img.width  * scale)
        const h = Math.round(img.height * scale)
        const canvas = document.createElement('canvas')
        canvas.width  = w
        canvas.height = h
        const isPng = file.type === 'image/png'
        canvas.getContext('2d').drawImage(img, 0, 0, w, h)
        const dataUrl = isPng ? canvas.toDataURL('image/png') : canvas.toDataURL('image/jpeg', 0.85)
        resolve({ base64: dataUrl.split(',')[1], contentType: isPng ? 'image/png' : 'image/jpeg' })
      }
      img.onerror = reject
      img.src = url
    })

  const handleLibraryUpload = async (file) => {
    if (!file) return
    setUploadingLib(true)
    try {
      const { base64, contentType } = await resizeImage(file)
      const res    = await apiFetch('/api/admin/upload-image', {
        method: 'POST',
        body: JSON.stringify({ tenantSlug: 'shared', fileName: file.name, fileData: base64, contentType, addToShared: true }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      const libRes  = await apiFetch('/api/admin/image-library')
      const libData = await libRes.json()
      setImageLibrary(Array.isArray(libData) ? libData : [])
    } catch (err) {
      setError(`Upload fehlgeschlagen: ${err.message}`)
    } finally {
      setUploadingLib(false)
    }
  }
  const removeRule = (idx) => { if (rules.length > 1) setRules(prev => prev.filter((_, i) => i !== idx)) }

  // ── PDF import ───────────────────────────────────────────────────────────
  // Imports a PDF as an ordered run of text/image blocks, preserving the
  // order content appeared on the page (rather than flattening to plain
  // text), and appends them to the rule list for review before saving.
  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload  = () => resolve(reader.result.split(',')[1])
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  const handlePdfImport = async (file) => {
    if (!file) return
    setImportingPdf(true)
    setError(null)
    try {
      const base64 = await readFileAsBase64(file)
      const res    = await apiFetch('/api/admin/rule-library/import-pdf', {
        method: 'POST',
        body: JSON.stringify({ fileData: base64 }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)

      const imported = data.entries.map(e => e.image_url
        ? { library_id: null, custom_text: null, custom_image_url: e.image_url, image_only: true }
        : { library_id: null, custom_text: e.text, custom_image_url: null, image_only: false }
      )
      setRules(prev => {
        const cleaned = prev.filter(r => r.library_id || r.custom_text?.trim() || (r.image_only && r.custom_image_url))
        return [...cleaned, ...imported]
      })
      setIsPdfImport(true)
      setDisplayOverride('document')

      if (data.truncated) {
        setError(`Nur die ersten ${imported.length} Bloecke wurden aus dem PDF importiert — bei Bedarf das PDF aufteilen und erneut importieren.`)
      }
    } catch (err) {
      setError(`PDF-Import fehlgeschlagen: ${err.message}`)
    } finally {
      setImportingPdf(false)
    }
  }

  const moveRule = (idx, direction) => {
    const newRules = [...rules]
    const target   = idx + direction
    if (target < 0 || target >= newRules.length) return
    ;[newRules[idx], newRules[target]] = [newRules[target], newRules[idx]]
    setRules(newRules)
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  const saveAbortRef = useRef(null)

  const handleSave = async () => {
    const selected = rules.filter(r => r.library_id || r.custom_text?.trim() || r.custom_image_url)
    if (selected.length === 0) { setError('Bitte mindestens eine Regel auswählen oder eingeben'); return }

    const ruleObjs = selected.map(r => {
      if (r.library_id) {
        const libRule = ruleLibrary.find(lr => lr.id === r.library_id)
        return { text: libRule?.translations?.de || '', image_url: libRule?.image_url || null, library_id: r.library_id }
      }
      if (r.image_only) return { text: null, image_url: r.custom_image_url, image_size: r.image_size || null }
      return { text: r.custom_text.trim(), image_url: r.custom_image_url || null, image_size: r.image_size || null }
    })

    const controller = new AbortController()
    saveAbortRef.current = controller

    setSaving(true)
    setError(null)
    setSuccess(false)
    try {
      const res = await apiFetch('/api/admin/safety-rules', {
        method: 'POST',
        body: JSON.stringify({
          tenantId:    selectedTenant,
          sourceLang:  'de',
          targetLangs: ['de','en','es','it','pl','nl','da','fr','hu','hr','pt','cs','bg','et','fi','el','lv','lt','no','ro','ru','sr','sk','sl','sv','tr','uk'],
          rules:       ruleObjs,
          display:     displayOverride || null,
          skipTranslation: isPdfImport && skipTranslation,
        }),
        signal: controller.signal,
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      if (err.name === 'AbortError') return
      setError(err.message)
    } finally {
      setSaving(false)
      saveAbortRef.current = null
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Sicherheitsregeln</h1>
          <p>Regeln aus der Bibliothek auswählen — Übersetzungen werden automatisch übernommen</p>
        </div>
      </div>

      {isOwner && tenants.length > 0 && (
        <div className="card sre-tenant-card">
          <div className="form-group sre-tenant-form-group">
            <label>Mandant</label>
            <select
              className="form-control"
              value={selectedTenant}
              onChange={e => {
                setSelectedTenant(e.target.value)
              }}
            >
              {tenants.map(t =>
                <option key={t.id} value={t.id}>{t.name}</option>
              )}
            </select>
          </div>
        </div>
      )}

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">✓ Sicherheitsregeln gespeichert</div>}

      <div className="card">
        <div className="section-title">Regeln</div>

        {ruleLibrary.length === 0 && !loading && (
          <div className="alert alert-error sre-empty-alert">
            Die Regelbibliothek ist leer. Bitte zuerst Regeln in der{' '}
            {isOwner
              ? <a href="/rule-library" className="sre-library-link">Regelbibliothek</a>
              : 'Regelbibliothek (Owner)'
            } hinzufügen.
          </div>
        )}

        {loading ? (
          <div className="loading-state"><span className="spinner" /></div>
        ) : (
          <>
            {isPdfImport ? (
              <>
                <div className="sre-pdf-preview">
                  {rules.map((rule, idx) => {
                    // A block can have an image, text, or both.
                    // image_only = true means the block came from the PDF as a
                    // pure image; false means it started as text (but the admin
                    // may have added an image to it manually).
                    const isImage = !!rule.custom_image_url
                    const isText  = !rule.image_only && rule.custom_text !== null && rule.custom_text !== undefined
                    if (!isImage && !isText) return null
                    return (
                      <React.Fragment key={idx}>
                        <div className="sre-pdf-insert-zone">
                          <button className="sre-pdf-insert-btn" onClick={() => insertRuleAt(idx)}>
                            + Textfeld einfügen
                          </button>
                        </div>
                        <div className="sre-pdf-block-wrap">
                          <div className="sre-pdf-block-content">
                            {isImage && (
                              <div className="sre-pdf-img-wrap">
                                <img
                                  src={rule.custom_image_url}
                                  alt=""
                                  className={`sre-pdf-preview-img${rule.image_size ? ` sre-pdf-img-${rule.image_size}` : ''}`}
                                />
                                <button
                                  className="sre-pdf-img-remove"
                                  onClick={() => updateCustomImageUrl(idx, null)}
                                  title="Bild entfernen"
                                >✕</button>
                                <div className="sre-pdf-img-sizes">
                                  {['25', '50', '75', '100'].map(size => (
                                    <button
                                      key={size}
                                      className={`sre-pdf-size-btn${(rule.image_size || '100') === size ? ' sre-pdf-size-btn--active' : ''}`}
                                      onClick={() => updateImageSize(idx, size === '100' ? null : size)}
                                    >{size}%</button>
                                  ))}
                                </div>
                              </div>
                            )}
                            {isText && (
                              <PdfTextBlock
                                initialHTML={rule.custom_text || ''}
                                onChange={html => updateCustomText(idx, html)}
                              />
                            )}
                          </div>
                          <div className="sre-pdf-block-controls">
                            <button onClick={() => moveRule(idx, -1)} disabled={idx === 0} title="Nach oben">↑</button>
                            <button onClick={() => moveRule(idx, 1)} disabled={idx === rules.length - 1} title="Nach unten">↓</button>
                            {isText && (
                              <button onClick={() => openImageModal(idx)} title={isImage ? 'Bild ersetzen' : 'Bild hinzufügen'}>🖼</button>
                            )}
                            <button onClick={() => removeRule(idx)} title="Entfernen">✕</button>
                          </div>
                        </div>
                      </React.Fragment>
                    )
                  })}
                  <div className="sre-pdf-insert-zone">
                    <button className="sre-pdf-insert-btn" onClick={() => insertRuleAt(rules.length)}>
                      + Textfeld einfügen
                    </button>
                  </div>
                </div>

                <div className="sre-toolbar-row">
                  <input
                    type="file" accept="application/pdf" className="input-hidden"
                    ref={pdfFileInputRef}
                    onChange={e => { handlePdfImport(e.target.files[0]); e.target.value = '' }}
                  />
                  <button
                    className="btn btn-ghost sre-add-btn"
                    onClick={() => pdfFileInputRef.current?.click()}
                    disabled={importingPdf}
                  >
                    {importingPdf
                      ? <><span className="spinner spinner-xs" /> PDF wird verarbeitet...</>
                      : '📄 PDF ersetzen'
                    }
                  </button>
                  <button
                    className="btn btn-ghost sre-add-btn"
                    onClick={() => {
                      setIsPdfImport(false)
                      setDisplayOverride(null)
                      setRules([{ library_id: null, custom_text: '', custom_image_url: null, image_only: false }])
                    }}
                  >
                    Auf manuelle Regeln wechseln
                  </button>
                </div>
              </>
            ) : (
              <>
                {rules.map((rule, idx) => {
                  const libRule = ruleLibrary.find(lr => lr.id === rule.library_id)
                  return (
                    <div key={idx} className="rule-row">
                      <div className="rule-number">{idx + 1}</div>
                      <div className="rule-body">
                        {rule.image_only ? (
                          <div className="sre-image-picker">
                            {rule.custom_image_url ? (
                              <div className="sre-image-preview">
                                <img src={rule.custom_image_url} alt="" className="sre-preview-img" />
                                <button className="btn btn-ghost sre-remove-btn" onClick={() => openImageModal(idx)}>
                                  ↻ Bild ersetzen
                                </button>
                              </div>
                            ) : (
                              <button className="btn btn-ghost sre-select-btn" onClick={() => openImageModal(idx)}>
                                🖼 Bild auswählen
                              </button>
                            )}
                          </div>
                        ) : (
                          <>
                            <select
                              className="form-control"
                              value={rule.library_id || ''}
                              onChange={e => selectRule(idx, e.target.value)}
                            >
                              <option value="">— Regel auswählen —</option>
                              {ruleLibrary.map(lr => (
                                <option key={lr.id} value={lr.id}>
                                  {(lr.translations?.de || '').substring(0, 90)}
                                </option>
                              ))}
                            </select>

                            {allowCustomRules && !rule.library_id && (
                              <>
                                <textarea
                                  className="form-control sre-custom-textarea"
                                  rows={2}
                                  placeholder="Oder eigenen Regeltext eingeben..."
                                  value={rule.custom_text || ''}
                                  onChange={e => updateCustomText(idx, e.target.value)}
                                />
                                <div className="sre-image-picker">
                                  {rule.custom_image_url ? (
                                    <div className="sre-image-preview">
                                      <img src={rule.custom_image_url} alt="" className="sre-preview-img" />
                                      <button className="btn btn-ghost sre-remove-btn" onClick={() => updateCustomImageUrl(idx, null)}>
                                        ✕ Bild entfernen
                                      </button>
                                    </div>
                                  ) : (
                                    <button className="btn btn-ghost sre-select-btn" onClick={() => openImageModal(idx)}>
                                      🖼 Bild auswählen (optional)
                                    </button>
                                  )}
                                </div>
                              </>
                            )}

                            {libRule?.image_url && (
                              <div className="rule-image-preview">
                                <img src={libRule.image_url} alt={`Regel ${idx + 1}`} />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <div className="rule-actions">
                        <button className="rule-btn" onClick={() => moveRule(idx, -1)} disabled={idx === 0} title="Nach oben">↑</button>
                        <button className="rule-btn" onClick={() => moveRule(idx, 1)} disabled={idx === rules.length - 1} title="Nach unten">↓</button>
                        <button className="rule-btn rule-btn-delete" onClick={() => removeRule(idx)} disabled={rules.length === 1} title="Löschen">✕</button>
                      </div>
                    </div>
                  )
                })}

                <div className="sre-toolbar-row">
                  <button className="btn btn-ghost sre-add-btn" onClick={addRule}>
                    + Regel hinzufügen
                  </button>

                  {allowCustomRules && (
                    <>
                      <button className="btn btn-ghost sre-add-btn" onClick={addImageBlock}>
                        + Bild
                      </button>

                      <input
                        type="file" accept="application/pdf" className="input-hidden"
                        ref={pdfFileInputRef}
                        onChange={e => { handlePdfImport(e.target.files[0]); e.target.value = '' }}
                      />
                      <button
                        className="btn btn-ghost sre-add-btn"
                        onClick={() => pdfFileInputRef.current?.click()}
                        disabled={importingPdf}
                      >
                        {importingPdf
                          ? <><span className="spinner spinner-xs" /> PDF wird verarbeitet...</>
                          : '📄 Aus PDF importieren'
                        }
                      </button>
                    </>
                  )}
                </div>
              </>
            )}

            {allowCustomRules && (
              <div className="sre-display-row">
                <label htmlFor="sre-display-select">Anzeige am Terminal</label>
                {isPdfImport ? (
                  <span className="sre-pdf-mode-badge">Dokument (aus PDF-Import)</span>
                ) : (
                  <select
                    id="sre-display-select"
                    className="form-control"
                    value={displayOverride || ''}
                    onChange={e => setDisplayOverride(e.target.value || null)}
                  >
                    <option value="">Automatisch (je nach Inhalt)</option>
                    <option value="grid">Kacheln</option>
                    <option value="document">Dokument</option>
                  </select>
                )}
              </div>
            )}

            <div className="sre-save-row">
              <div className="sre-save-col">
                {isPdfImport && !saving && (
                  <label className="sre-skip-translation-label">
                    <input
                      type="checkbox"
                      checked={skipTranslation}
                      onChange={e => setSkipTranslation(e.target.checked)}
                    />
                    Nur auf Deutsch speichern (Übersetzung später)
                  </label>
                )}
                <div className="sre-save-btns">
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    {saving
                      ? <><span className="spinner spinner-sm" /> {isPdfImport && !skipTranslation ? 'Übersetzen & speichern…' : 'Speichern…'}</>
                      : 'Speichern'}
                  </button>
                  {saving && (
                    <button
                      className="btn btn-ghost"
                      onClick={() => { saveAbortRef.current?.abort(); setSaving(false) }}
                    >
                      Abbrechen
                    </button>
                  )}
                </div>
                {saving && isPdfImport && !skipTranslation && (
                  <p className="sre-save-hint">
                    Inhalte werden in 27 Sprachen übersetzt – das kann 15–25 Minuten dauern.
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {/* ── Image library modal ─────────────────────────────────────────── */}
      {showImageModal && (
        <div className="modal-overlay" onClick={() => setShowImageModal(false)}>
          <div className="modal modal--wide" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Bild auswählen</h2>
              <button className="modal-close" onClick={() => setShowImageModal(false)}>✕</button>
            </div>

            <div className="image-lib-upload">
              <input
                type="file" accept="image/*" className="input-hidden"
                ref={libFileInputRef}
                onChange={e => handleLibraryUpload(e.target.files[0])}
              />
              <button
                className="btn btn-ghost"
                onClick={() => libFileInputRef.current?.click()}
                disabled={uploadingLib}
              >
                {uploadingLib
                  ? <><span className="spinner spinner-xs" /> Wird hochgeladen...</>
                  : '⬆ Neues Bild hochladen'
                }
              </button>
            </div>

            {loadingImages ? (
              <div className="loading-state"><span className="spinner" /></div>
            ) : imageLibrary.length === 0 ? (
              <p className="sre-modal-empty">
                Noch keine Bilder in der Bibliothek.
              </p>
            ) : (
              <div className="image-lib-grid">
                {imageLibrary.map(img => (
                  <div
                    key={img.id}
                    className="image-lib-item"
                    onClick={() => {
                      updateCustomImageUrl(activeImageIdx, img.url)
                      setShowImageModal(false)
                    }}
                  >
                    <img src={img.url} alt={img.name} />
                    <div className="image-lib-item__name">{img.name}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowImageModal(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {selectedTenant && (
        <VersionHistoryPanel table="safety_rules" tenantId={selectedTenant} onRestored={loadRules} />
      )}
    </div>
  )
}

export default SafetyRulesEditor
